import { Container } from "react-bootstrap";
import { motion } from "framer-motion";
import { People, Award, Heart, Globe } from "react-bootstrap-icons";
import { Seo } from "../components/Seo";
import { generateStaticPageSEO } from "../lib/seo/metaGenerators";

export function About() {
  const features = [
    { icon: People, title: "Our Team", description: "A dedicated team of food enthusiasts and tech innovators working together to revolutionize food delivery." },
    { icon: Award, title: "Our Mission", description: "To connect people with the best local food experiences while supporting local restaurants and communities." },
    { icon: Heart, title: "Our Values", description: "Quality, reliability, and customer satisfaction are at the heart of everything we do." },
    { icon: Globe, title: "Our Impact", description: "Creating sustainable food delivery solutions that benefit both customers and local businesses." }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.42 } }
  };

  return (
    <>
    <Seo {...generateStaticPageSEO({
      title: "About Us",
      description: "Learn about Teco's mission to connect customers with local restaurants across Kenya through fast, reliable food delivery.",
      path: "/about",
    })} />
    <Container className="about-shell" as="section" aria-label="About Teco">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={containerVariants} className="about-inner">

        <motion.header variants={itemVariants} className="about-header">
          <h1 className="about-title">About Teco Food Delivery</h1>
          <p className="about-lead micro">We're more than a delivery app — we're your partner for discovering the best local culinary experiences, fast and reliably.</p>
        </motion.header>

        <motion.div variants={itemVariants} className="features-grid">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <article className="feature" key={i} role="article" aria-label={f.title}>
                <motion.div variants={itemVariants} className="feature-card">
                  <div className="feature-icon">
                    <Icon size={20} className="icon-line" />
                  </div>
                  <div className="feature-body">
                    <h3 className="feature-title">{f.title}</h3>
                    <p className="feature-desc micro">{f.description}</p>
                  </div>
                </motion.div>
              </article>
            );
          })}
        </motion.div>

        <motion.section variants={itemVariants} className="story" aria-label="Our Story">
          <div className="story-card">
            <h2 className="story-title">Our Story</h2>
            <p className="story-text micro">Founded in 2024, Teco started with a simple idea: make great food easy to access. Today we connect thousands of customers to local restaurants while prioritizing quality, speed and sustainability.</p>
          </div>
        </motion.section>

      </motion.div>

      <style>{`
        :root{--maxw:980px;--gap:12px;--radius:12px;--muted:0,0,0;--accent:26,115,232}
        .about-shell{padding:20px 12px}
        .about-inner{max-width:var(--maxw);margin:0 auto;display:flex;flex-direction:column;gap:18px}

        .about-header{display:flex;flex-direction:column;gap:8px;text-align:center;padding:6px}
        .about-title{margin:0;font-size:22px;font-weight:800;letter-spacing:-0.01em;color:rgba(var(--muted),0.92)}
        .about-lead{margin:0;color:rgba(0,0,0,0.66);font-size:14px;line-height:20px;max-width:760px;margin-left:auto;margin-right:auto}

        /* Features grid: 1 column small, 2 columns medium, 4 on large — pixel-perfect alignment */
        .features-grid{display:grid;grid-template-columns:1fr;gap:12px;align-items:stretch}
        @media(min-width:540px){.features-grid{grid-template-columns:repeat(2,1fr);gap:14px}}
        @media(min-width:1024px){.features-grid{grid-template-columns:repeat(4,1fr);gap:16px}}

        .feature{display:flex;min-height:100%}
        .feature-card{display:flex;flex:1;gap:12px;align-items:flex-start;padding:12px;border-radius:calc(var(--radius) + 2px);background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.06);transition:transform 160ms cubic-bezier(.2,.9,.2,1),box-shadow 160ms cubic-bezier(.2,.9,.2,1)}

        .feature-icon{flex:0 0 44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;background:rgba(var(--accent),0.06)}
        .icon-line{stroke:rgba(0,0,0,0.72);stroke-width:1.2}

        .feature-title{margin:0;font-size:14px;font-weight:700}
        .feature-desc{margin:0;color:rgba(0,0,0,0.66);font-size:13px;line-height:18px}

        /* Soft elevation only on hover for pointer devices */
        .feature-card:hover{transform:translateY(-4px);box-shadow:0 10px 30px rgba(11,22,40,0.04)}

        .story-card{padding:14px;border-radius:14px;background:rgba(255,255,255,0.98);border:1px solid rgba(0,0,0,0.05)}
        .story-title{margin:0;font-size:18px;font-weight:700}
        .story-text{margin:0;color:rgba(0,0,0,0.66);font-size:14px;line-height:20px}

        /* Respect reduced motion preferences */
        @media (prefers-reduced-motion: reduce){.feature-card,.about-inner{transition:none!important}}

        /* 4K pixel precision */
        .about-inner,.feature-card{transform:translateZ(0);backface-visibility:hidden}

        /* Small screen polish */
        @media(max-width:420px){.about-title{font-size:20px}.feature-icon{flex:0 0 40px;height:40px}.feature-card{padding:10px}}
      `}</style>
    </Container>
    </>
  );
}